import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { CommonModule, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { LocationSelectors } from '@states/location/location.selector-types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiKitModule } from '../../../../../../shared/ui-kit/ui-kit.module';
import { TruncateDirective } from '../../../../../../shared/directives/truncate.directive';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { VehicleModels } from '@models/vehicle.model';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel } from '@ui';
import { ofType } from '@ngrx/effects';
import { SharedEffects } from '@effects/shared.effects';
import { UiVehicleIconComponent } from '../../../../../../shared/ui-kit/ui-vehicle-icon/ui-vehicle-icon.component';
import { SelectionChangeEvent, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createVehiclesTableConfig, VehicleCellContext } from './vehicles-table.util';
import { VehicleEditModalComponent } from '../vehicle-edit-modal/vehicle-edit-modal.component';

@Component({
  selector: 'app-vehicles-table',
  imports: [CommonModule, TranslatePipe, UpperCasePipe, UiKitModule, TruncateDirective, TitleCasePipe, UiVehicleIconComponent, UiDatasetTableComponent],
  templateUrl: './vehicles-table.component.html',
  styleUrl: './vehicles-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehiclesTableComponent implements OnInit {
  readonly vehicles = input.required<VehicleModels.Vehicle[]>();
  readonly canEdit = input(true);
  readonly checkboxEnabled = input<boolean | undefined>(undefined);
  readonly isRowDisabled = input<((row: VehicleModels.Vehicle) => boolean) | undefined>(undefined);
  readonly deleteTooltip = input('VEHICLES.TOOLTIPS.REMOVE');
  readonly onRemove = input<((vehicle: VehicleModels.Vehicle) => void) | undefined>(undefined);
  readonly selectionChange = output<SelectionChangeEvent<VehicleModels.Vehicle>>();

  protected readonly imageCellTemplate = viewChild<TemplateRef<VehicleCellContext>>('imageCell');
  protected readonly licensePlateCellTemplate = viewChild<TemplateRef<VehicleCellContext>>('licensePlateCell');
  protected readonly ownerCellTemplate = viewChild<TemplateRef<VehicleCellContext>>('ownerCell');
  protected readonly makeCellTemplate = viewChild<TemplateRef<VehicleCellContext>>('makeCell');
  protected readonly colorCellTemplate = viewChild<TemplateRef<VehicleCellContext>>('colorCell');

  private readonly locationNamesByEdgeId = this.store$.selectSignal(LocationSelectors.selectLocationNamesByEdgeId);

  public readonly tableConfig = computed(() => {
    const imageTemplate = this.imageCellTemplate();
    const licensePlateTemplate = this.licensePlateCellTemplate();
    const ownerTemplate = this.ownerCellTemplate();
    const makeTemplate = this.makeCellTemplate();
    const colorTemplate = this.colorCellTemplate();

    if (!imageTemplate || !licensePlateTemplate || !ownerTemplate || !makeTemplate || !colorTemplate) {
      return null;
    }

    return createVehiclesTableConfig({
      imageCellTemplate: imageTemplate,
      licensePlateCellTemplate: licensePlateTemplate,
      ownerCellTemplate: ownerTemplate,
      makeCellTemplate: makeTemplate,
      colorCellTemplate: colorTemplate,
      getLocationName: (row) => this.getLocationNameFormatted(row),
      canEdit: () => this.canEdit(),
      checkboxEnabled: this.checkboxEnabled() ?? this.canEdit(),
      actionsEnabled: this.canEdit(),
      isRowDisabled: this.isRowDisabled(),
      deleteTooltip: this.deleteTooltip(),
    });
  });

  constructor(
    private store$: Store,
    private destroyRef: DestroyRef,
    private translateService: TranslateService,
    private sharedEffects: SharedEffects,
    private dialog: MatDialog,
  ) {}

  public ngOnInit() {
    this.sharedEffects.confirmation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(SharedActions.showConfirmModalResultConfirm)
      )
      .subscribe((result) => {
        const index = result?.params['vehicleIndex'];
        const plates = result?.params['plates'];
        
        if (index !== undefined && index !== null) {
          const vehicle = this.vehicles()[index];
          if (vehicle) {
            this.store$.dispatch(VehiclesActions.removeVehicle({ vehicle }));
          }
        }
        
        if (plates) {
          this.store$.dispatch(VehiclesActions.removeVehiclesByPlates({ plates }));
        }
      });
  }

  public selectedPlatesRows = signal<Set<string>>(new Set());
  public readonly forcedSelectedIds = signal<(string | number)[]>([]);

  public applyForcedSelection(plates: string[]): void {
    this.forcedSelectedIds.set(plates);
    this.selectedPlatesRows.set(new Set(plates));
  }

  public onSelectionChange(event: SelectionChangeEvent<VehicleModels.Vehicle>): void {
    const plates = new Set(event.selectedIds.map((id) => String(id)));
    this.selectedPlatesRows.set(plates);
    this.selectionChange.emit(event);
  }

  private getLocationNameFormatted(vehicle: VehicleModels.Vehicle): string {
    const namesMap = this.locationNamesByEdgeId();
    return namesMap?.[vehicle.edgeId] ?? '—';
  }

  public onRowAction(event: {
    actionId: string;
    row: VehicleModels.Vehicle;
  }): void {
    if (!this.canEdit()) {
      return;
    }

    if (event.actionId === 'edit') {
      this.openEditModal(event.row);
    } else if (event.actionId === 'delete') {
      this.remove(event.row);
    }
  }

  public openEditModal(vehicle: VehicleModels.Vehicle): void {
    this.dialog.open(VehicleEditModalComponent, {
      data: { vehicle },
      panelClass: 'vehicle-edit-modal',
      width: '90vw',
      maxWidth: '500px',
    });
  }

  public remove(row: VehicleModels.Vehicle) {
    if (!this.canEdit()) {
      return;
    }

    const customRemove = this.onRemove();
    if (customRemove) {
      customRemove(row);
      return;
    }

    const vehicleIndex = this.vehicles().findIndex((v) => v.plate === row.plate);
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          msg: this.translateService.instant('DELETE_VEHICLE_MODAL.MSG', {
            plate: row.plate,
          }),
          title: this.translateService.instant('DELETE_VEHICLE_MODAL.TITLE'),
          confirm: this.translateService.instant('CONFIRM_MODALS.COMMON_YES'),
          disableClose: true,
          params: {
            vehicleIndex,
          },
        },
      }),
    );
  }
}
